import {DestroyRef, inject, Injectable, signal} from '@angular/core';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {environment} from '../../../environments/environment';
import {Observable, Subject, take} from 'rxjs';

/**
 * Long-lived chat channel at /ws/chat/presence. An open socket marks the user
 * present and carries server-pushed chat events (currently unread-count
 * updates); the AdminLayout REST poll is only the fallback while it is down.
 */
@Injectable({providedIn: 'root'})
export class ChatPresenceService {
  private readonly oidc = inject(OidcSecurityService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly unreadSubject = new Subject<number>();
  /** Server-pushed chat unread count; subscribers receive new values as they arrive. */
  readonly unreadCount$: Observable<number> = this.unreadSubject;

  /** True while the socket is open; the header REST poll backs off while this is true. */
  private readonly _connected = signal(false);
  readonly connected = this._connected.asReadonly();

  /** Ping cadence to keep the TCP connection warm through idle-timeout proxies/LBs. */
  private static readonly KEEP_ALIVE_INTERVAL_MS = 25_000;

  private socket: WebSocket | null = null;
  private socketGeneration = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;
  private tokenChangeSub: { unsubscribe(): void } | null = null;

  private readonly visibilityHandler = () => {
    if (!document.hidden) {
      this.openSocket();
    }
  };

  constructor() {
    this.tokenChangeSub = this.oidc.isAuthenticated$.subscribe((authenticated) => {
      if (authenticated) {
        this.openSocket();
      }
    });
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.tokenChangeSub?.unsubscribe();
      this.stopKeepAlive();
      this.cancelReconnect();
      this.closeSocket(true);
    });
  }

  connect(): void {
    this.openSocket();
  }

  /** Subscribe to server-pushed chat unread counts. Returns an unsubscribe function. */
  onUnreadCount(handler: (count: number) => void): () => void {
    const subscription = this.unreadSubject.subscribe(handler);
    return () => subscription.unsubscribe();
  }

  private openSocket(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.cancelReconnect();
    this.closeSocket(true);

    void this.oidc.getAccessToken().pipe(take(1)).subscribe((token) => {
      const generation = ++this.socketGeneration;
      if (this.socket) {
        return;
      }
      const url = new URL(`${environment.serverUrl}/ws/chat/presence`, window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      if (token) {
        url.searchParams.set('access_token', token);
      }

      const socket = new WebSocket(url.toString());
      this.socket = socket;
      this.log('opening socket', {generation, reconnectAttempts: this.reconnectAttempts});

      socket.onopen = () => {
        this.reconnectAttempts = 0;
        this._connected.set(true);
        this.log('socket OPEN');
        this.startKeepAlive(socket);
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as Record<string, unknown>;
          if (message['unread-count'] === true && typeof message['count'] === 'number') {
            this.unreadSubject.next(message['count']);
          }
        } catch {
          // Ignore malformed frames.
        }
      };

      socket.onclose = (event: CloseEvent) => {
        if (generation !== this.socketGeneration) {
          this.log('socket CLOSED (superseded)', {code: event.code});
          return;
        }
        this.log('socket CLOSED', {code: event.code, reason: event.reason});
        this.stopKeepAlive();
        this._connected.set(false);
        this.socket = null;
        this.scheduleReconnect();
      };

      socket.onerror = () => {
        this.log('socket ERROR (onclose follows)');
      };
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || document.hidden) {
      return;
    }
    const delay = Math.min(60_000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts = Math.min(this.reconnectAttempts + 1, 6);
    this.log('scheduling reconnect', {delayMs: delay, attempt: this.reconnectAttempts});
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Periodic text ping so idle-timeout proxies keep the socket open. */
  private startKeepAlive(socket: WebSocket): void {
    this.stopKeepAlive();
    this.keepAliveTimer = setInterval(() => {
      if (this.socket === socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send('ping');
        } catch {
          // Socket closing; onclose stops the timer.
        }
      }
    }, ChatPresenceService.KEEP_ALIVE_INTERVAL_MS);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveTimer !== null) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  private closeSocket(supersede: boolean): void {
    if (supersede) {
      this.socketGeneration++;
    }
    this.stopKeepAlive();
    this._connected.set(false);
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close(1000);
      }
    }
  }

  private log(message: string, detail?: unknown): void {
    if (!environment.production) {
      console.log(`[chat-presence] ${message}`, detail ?? '');
    }
  }
}
